import { DashboardWidgetType } from '@/types/dashboards/dashboard.types';
import { NPS_WIDGET_TYPE, getNpsTimeframeOptions } from '@/dashboards-v2/widgets/nps-widget/nps-widget-options';
import { FilterSelections } from './router-query-filters';
import { NO_PRODUCT_AREA_AGG_ID, NO_PRODUCT_AREA_OPTION_ID } from './groups';
import { toAggMetadataFilters } from './metadata-filter';
import { getValidPeriodsForDateRange } from '@/utils/date-range.utils';
import { DownstreamDashboardFilterMap } from './filter-map';
import { migrateLegacyAppIdToArray } from '@/apps/apps.utils';
import { isEntitySourceKind } from '@/dashboards-v2/components/add-to-dashboard/widget-config-export';
import { isPlatformApp } from '@pendo/middleware';
import { FilterType } from '@/types/analytics/analytics.types';
import { DWM_CHART_TYPES } from '@/charts/widget-configs/dwm-bar/dwm-chart-types';

/**
 * optionally supply a reason why a filter was not applied for use in tooltip
 * NOTE: if a filter is omitted, but no reason is provided, it will default to saying the widget
 * does not support the setting
 */
type InactiveReasons = Partial<Record<keyof FilterSelections, string>>;
type ActiveReasons = Partial<Record<keyof DownstreamDashboardFilterMap, string>>;
type Reasons = InactiveReasons & ActiveReasons;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Filters = FilterSelections & Partial<Record<keyof DownstreamDashboardFilterMap, any>>;
type ModifierResponse = { filters: Filters; reasons: Reasons };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type WidgetDefinition = Record<string, any>;

type FilterModifier = (selections: FilterSelections, definition: WidgetDefinition) => ModifierResponse;

export const getModifier = (selections = {}, reasons = {}): ModifierResponse => ({
    filters: { ...selections },
    reasons,
});
const inactiveReason = (filterType: string, reason: string) => `${filterType} was not applied because ${reason}`;
const activeReason = (filterType: string, reason: string) => `${filterType} was applied because ${reason}`;
const isMultiAppDefault = (appId: unknown) => typeof appId === 'string' && appId?.includes('expandAppIds');
const hasMultipleAppsSelected = (appIds: unknown) => Array.isArray(appIds) && appIds.length > 1;

/**
 * MUTATION HELPERS
 * Takes a ModifierResponse (+ any addl params) and adds props to filters/reasons
 */
export const addAppIfNotPlatform = ({ filters, reasons }: ModifierResponse, appId?: string | number | number[]) => {
    if (!appId) return;

    appId = migrateLegacyAppIdToArray(appId);

    if (!isPlatformApp(appId[0])) {
        filters.appId = appId;
    } else {
        reasons.appId = inactiveReason('App', 'this widget does not support platform app calculations');
    }
};

// Guide-effectiveness widgets compute against a single app, so a multi-app or
// platform-default app selection can't be honored — surface a reason instead.
// A single concrete app still applies.
export const applySingleApp = ({ filters, reasons }: ModifierResponse, appId: unknown) => {
    if (!appId) return;

    if (isMultiAppDefault(appId) || hasMultipleAppsSelected(appId)) {
        reasons.appId = inactiveReason('App', 'this widget does not support multiple app calculations');
    } else {
        filters.appId = Array.isArray(appId) ? appId[0] : appId;
    }
};

export const removeSinceGuideCreationIfDateRange = ({ filters }: ModifierResponse) => {
    if (filters.dateRange) {
        filters.sinceGuideCreation = false;
    }
};

export const setToValidPeriodForDateRange = ({ filters, reasons }: ModifierResponse, widgetDef: WidgetDefinition) => {
    const { dateRange } = filters;
    if (!dateRange) return;

    const { period: widgetPeriod } = widgetDef;
    const validPeriods = getValidPeriodsForDateRange(dateRange.timeSeries);
    if (validPeriods.includes(widgetPeriod)) return;

    const period = ['hourly', 'daily'].includes(widgetPeriod) ? validPeriods[0] : validPeriods[validPeriods.length - 1];
    filters.period = period;
    reasons.period = activeReason(
        'Period',
        `the chosen frequency (${widgetPeriod}) is not compatible with the chosen date range`,
    );
};

export const addDateRangeIfNotExceeds = (
    { filters, reasons }: ModifierResponse,
    dateRange: FilterSelections['dateRange'],
    maxDays: number,
) => {
    if (!dateRange) return;

    const { count } = dateRange.timeSeries?.spec || {};

    if (count && count > maxDays) {
        reasons.dateRange = inactiveReason('Date', `the chosen date range is greater than ${maxDays} days`);
    } else {
        filters.dateRange = dateRange;
    }
};

export const applyMetadataFilter = ({ filters }: ModifierResponse) => {
    const metadataFilters = toAggMetadataFilters(filters.metadataFilter);
    delete filters.metadataFilter;

    if (metadataFilters.length) {
        filters.metadataFilters = metadataFilters;
    }
};

export const ADDITIVE_FILTER_KEYS: string[] = ['metadataFilters'];

// The Product Areas overview widgets store their scope under `productAreaIds`,
// so they need the opt-in to receive `groups` at all; they read it as an
// override (resolveEffectiveProductAreaIds).
export const OPT_IN_FILTER_KEYS: ReadonlyMap<string, ReadonlySet<DashboardWidgetType>> = new Map([
    ['appId', new Set<DashboardWidgetType>(['countableMetric', 'countableChart'])],
    ['groups', new Set<DashboardWidgetType>(['scatterChart', 'visitorOverview', 'objectOverview', 'featureAdoption', 'guideEffectivenessMetrics', 'guideEffectivenessPerformance', 'guideEngagement', 'guideOverview', 'mostSeenGuides', 'tagOverview', 'countableMetric', 'countableChart', 'pageFeatureUseOverTime', 'productAreasOverviewMetrics', 'productAreasOverviewChart'])],
]);

export const isFilterAppliedToWidget = (
    key: string,
    widgetType: DashboardWidgetType,
    definition: WidgetDefinition,
): boolean => {
    const optedInTypes = OPT_IN_FILTER_KEYS.get(key);
    if (optedInTypes) return optedInTypes.has(widgetType);

    return Object.prototype.hasOwnProperty.call(definition, key) || ADDITIVE_FILTER_KEYS.includes(key);
};

export const dropGroupsForExplicitEvents = (
    { filters, reasons }: ModifierResponse,
    widgetDef: WidgetDefinition,
) => {
    if (!filters.groups?.length) return;
    if (!Object.keys(widgetDef?.events || {}).length) return;

    delete filters.groups;
    reasons.groups = inactiveReason('Product Area', 'this widget is scoped to specific events');
};

export const dropUnassignedGroupScope = ({ filters, reasons }: ModifierResponse) => {
    if (!filters.groups?.length) return;

    const assigned = filters.groups.filter(
        (groupId: string) => groupId !== NO_PRODUCT_AREA_OPTION_ID && groupId !== NO_PRODUCT_AREA_AGG_ID,
    );
    if (assigned.length === filters.groups.length) return;

    if (assigned.length) {
        filters.groups = assigned;
    } else {
        delete filters.groups;
    }

    reasons.groups = inactiveReason('No Product Area', 'this widget only reports on assigned product areas');
};

export const intersectGroupsWithOwnScope = (
    { filters }: ModifierResponse,
    widgetDef: WidgetDefinition,
) => {
    if (!filters.groups?.length) return;
    if (Object.keys(widgetDef?.events || {}).length) return;

    const ownGroups: string[] = Array.isArray(widgetDef?.groups) ? widgetDef.groups : [];
    if (!ownGroups.length) return;

    const ownGroupSet = new Set(ownGroups);
    filters.groups = filters.groups.filter((groupId: string) => ownGroupSet.has(groupId));
};

/**
 * REUSED MODIFIERS
 */

export const withMetadataFilter = (modifier: FilterModifier): FilterModifier => (selections, widgetDef) => {
    const response = modifier(selections, widgetDef);
    applyMetadataFilter(response);

    return response;
};

export const productAreasOverviewModifier: FilterModifier = (selections) => {
    const modifier = getModifier(selections);

    dropUnassignedGroupScope(modifier);

    return modifier;
};

export const pesModifier: FilterModifier = ({ appId, dateRange, ...rest }) => {
    const { filters, reasons } = getModifier(rest);

    applySingleApp({ filters, reasons }, appId);
    addDateRangeIfNotExceeds({ filters, reasons }, dateRange, 180);

    return { filters, reasons };
};

export const frequencyModifier: FilterModifier = (selections, widgetDef) => {
    const modifier = getModifier(selections);

    setToValidPeriodForDateRange(modifier, widgetDef);

    return modifier;
};

// Source-scoped exports (Add to Dashboard from an entity detail page) keep the app
// their entity belongs to; the dashboard App filter can't be honored.
export const perWeekChartModifier: FilterModifier = (selections, widgetDef) => {
    if (!isEntitySourceKind(widgetDef?.source?.kind)) {
        return frequencyModifier(selections, widgetDef);
    }

    const { appId, ...rest } = selections;
    const modifier = frequencyModifier(rest, widgetDef);
    if (appId !== undefined) {
        modifier.reasons.appId = inactiveReason('App', 'this widget always shows data for its selected event');
    }

    return modifier;
};

export const singleAppModifier: FilterModifier = ({ appId, ...rest }) => {
    const modifier = getModifier(rest);

    applySingleApp(modifier, appId);

    return modifier;
};

const webAnalyticsBreakdownTableModifier: FilterModifier = ({ appId, ...rest }) => {
    const modifier = getModifier(rest);

    if (hasMultipleAppsSelected(appId)) {
        modifier.reasons.appId = inactiveReason('App', 'this widget does not support multiple app calculations');
    } else if (appId) {
        modifier.filters.appId = appId;
    }

    return modifier;
};

const topEntitiesModifier: FilterModifier = ({ appId, ...rest }) => {
    const { filters, reasons } = getModifier(rest);

    if (appId !== undefined) {
        reasons.appId = inactiveReason('App', 'this widget always shows data for its selected event');
    }

    return { filters, reasons };
};

const SURVEY_METADATA_FILTER_TYPES = new Set<string>([FilterType.Visitor, FilterType.Account]);

const surveyResponsesTableModifier: FilterModifier = ({ appId, ...rest }) => {
    const { filters, reasons } = getModifier(rest);

    if (appId !== undefined) {
        reasons.appId = inactiveReason('App', 'this widget always shows data for its survey');
    }

    const selected = filters.metadataFilter ?? [];
    const compatible = selected.filter(
        (filter) => filter.filterType != null && SURVEY_METADATA_FILTER_TYPES.has(filter.filterType),
    );
    if (compatible.length < selected.length) {
        filters.metadataFilter = compatible;
        reasons.metadataFilter = inactiveReason('Metadata', 'this widget only supports visitor and account metadata');
    }

    applyMetadataFilter({ filters, reasons });

    return { filters, reasons };
};

/**
 * Drops every dashboard filter selection and surfaces an inactive-reason for each.
 * Use for widgets that always source their filter state from another place (e.g. a
 * saved report definition) rather than the dashboard.
 */
const reportDrivenModifier = (reason: string): FilterModifier => ({
    appId,
    dateRange,
    segmentId,
    account,
    groups,
}) => {
    const { filters, reasons } = getModifier();

    if (appId !== undefined) reasons.appId = inactiveReason('App', reason);
    if (dateRange !== undefined) reasons.dateRange = inactiveReason('Date', reason);
    if (segmentId !== undefined) reasons.segmentId = inactiveReason('Segment', reason);
    if (account !== undefined) reasons.account = inactiveReason('Account', reason);
    if (groups !== undefined) reasons.groups = inactiveReason('Product Area', reason);

    return { filters, reasons };
};

// The guides-targeting table lists guide configuration, not usage data — no
// dashboard filter (including the additive metadata filter) can apply to it.
const guidesTargetingModifier: FilterModifier = (selections, widgetDef) => {
    const reason = 'this widget lists guides that target its selected event';
    const response = reportDrivenModifier(reason)(selections, widgetDef);
    if (selections.metadataFilter !== undefined) {
        response.reasons.metadataFilter = inactiveReason('Metadata', reason);
    }

    return response;
};

const dwmChartModifier: FilterModifier = (selections, widgetDef) => {
    if (widgetDef?.chartType === DWM_CHART_TYPES.BAR) {
        return withMetadataFilter((barSelections) => getModifier(barSelections))(selections, widgetDef);
    }

    const response = getModifier(selections);
    if (selections.metadataFilter !== undefined) {
        delete response.filters.metadataFilter;
        response.reasons.metadataFilter = inactiveReason(
            'Metadata',
            'the Trend chart type does not support metadata filters',
        );
    }

    return response;
};

/**
 * FILTER MODIFIER LIST
 */
const filterModifiers: { [k in DashboardWidgetType]?: FilterModifier } = {
    dwmChart: dwmChartModifier,
    pageFeatureUseOverTime: withMetadataFilter(frequencyModifier),
    perWeekChart: withMetadataFilter(perWeekChartModifier),
    featureAdoption: withMetadataFilter((selections, widgetDef) => {
        const modifier = getModifier(selections);
        dropGroupsForExplicitEvents(modifier, widgetDef);

        return modifier;
    }),
    countableMetric: withMetadataFilter((selections, widgetDef) => {
        const modifier = getModifier(selections);
        intersectGroupsWithOwnScope(modifier, widgetDef);

        return modifier;
    }),
    countableChart: withMetadataFilter((selections, widgetDef) => {
        const modifier = getModifier(selections);
        intersectGroupsWithOwnScope(modifier, widgetDef);

        return modifier;
    }),
    visitorAccountKeyMetrics: withMetadataFilter((selections) => getModifier(selections)),
    objectAnalyticsKeyMetrics: withMetadataFilter((selections) => getModifier(selections)),
    objectAnalyticsChart: withMetadataFilter((selections) => getModifier(selections)),
    webAnalyticsChart: withMetadataFilter((selections) => getModifier(selections)),
    webAnalyticsKeyMetrics: withMetadataFilter((selections) => getModifier(selections)),
    productAreasOverviewMetrics: withMetadataFilter(productAreasOverviewModifier),
    productAreasOverviewChart: withMetadataFilter(productAreasOverviewModifier),
    appHealthTable: withMetadataFilter((selections) => getModifier(selections)),
    webAnalyticsBreakdownTable: withMetadataFilter(webAnalyticsBreakdownTableModifier),
    topVisitors: withMetadataFilter(topEntitiesModifier),
    topAccounts: withMetadataFilter(topEntitiesModifier),
    featuresTargeting: withMetadataFilter(topEntitiesModifier),
    guidesTargeting: guidesTargetingModifier,
    guideEngagement: frequencyModifier,
    guideEffectivenessMetrics: singleAppModifier,
    guideEffectivenessPerformance: singleAppModifier,
    aiAgentSuggestedPromptsChartWidget: frequencyModifier,
    pesTrending: pesModifier,
    productEngagementScore: pesModifier,
    aiAgentTopSuggestedPromptsWidget: reportDrivenModifier(
        'this widget always uses the filters from its source report',
    ),
    npsWidget: ({ appId, ...rest }, { widgetType, timeframe }) => {
        const { filters, reasons } = getModifier(rest);

        if (appId) {
            reasons.appId = inactiveReason('App', 'its value is derived from the chosen NPS Survey');
        }
        if (filters.dateRange && widgetType === NPS_WIDGET_TYPE.LINE_GRAPH) {
            const timeframeOptions = getNpsTimeframeOptions(filters.dateRange.timeSeries.spec.count);
            if (!timeframeOptions.includes(timeframe)) {
                filters.timeframe = timeframeOptions[0];
                reasons.timeframe = activeReason(
                    'Timeframe',
                    `the chosen frequency (${timeframe}) is not compatible with the chosen date range`,
                );
            }
        }

        removeSinceGuideCreationIfDateRange({ filters, reasons });

        return { filters, reasons };
    },
    pollResults: (selections) => {
        const response = getModifier(selections);
        removeSinceGuideCreationIfDateRange(response);

        return response;
    },
    retentionByUsage: ({ segmentId, appId, ...rest }) => {
        const { filters, reasons } = getModifier(rest);

        if (segmentId) {
            if (['anonymousVisitorsOnly', 'everyone'].includes(segmentId)) {
                const unsupportedSegment = segmentId === 'everyone' ? 'Everyone' : 'Everyone (Anonymous Visitors Only)';
                reasons.segmentId = inactiveReason(
                    'Segment',
                    `the setting <i>${unsupportedSegment}</i> is not supported by this widget`,
                );
            } else {
                filters.segmentId = segmentId;
            }
        }
        addAppIfNotPlatform({ filters, reasons }, appId);

        return { filters, reasons };
    },
    timeToFirstUse: ({ appId, ...rest }) => {
        const modifier = getModifier(rest);
        addAppIfNotPlatform(modifier, appId);

        return modifier;
    },
    scatterChart: (selections, widgetDef) => {
        const modifier = getModifier(selections);
        dropGroupsForExplicitEvents(modifier, widgetDef);

        return modifier;
    },
    visitorOverview: withMetadataFilter((selections, widgetDef) => {
        const modifier = getModifier(selections);
        dropGroupsForExplicitEvents(modifier, widgetDef);

        return modifier;
    }),
    objectOverview: withMetadataFilter((selections, widgetDef) => {
        const modifier = getModifier(selections);
        dropGroupsForExplicitEvents(modifier, widgetDef);

        return modifier;
    }),
    surveyResponsesTable: surveyResponsesTableModifier,
    tagOverview: (selections) => getModifier(selections),
};

export default filterModifiers;
